import React, { Fragment } from 'react';
import dayjs from 'dayjs';
import { Button, Col, Form, FormGroup, InputGroup, Row } from 'react-bootstrap';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, useLoadingSpinnerOnFullContainer } from 'App/components/utils/LoadingSpinner';
import { useGarantieMaxActions, IProductDataGM } from '../../GarantieMax';
import {
  ILeadData,
  IProductDataForInvoice,
  IPremiumCalculationStateGM,
  ITariffDataStep,
  IBFProductVariable,
} from 'Services/widgets/interfaces';
import { useAppAlertService } from 'App/components/utils/alerts/AppAlertService';
import { useWidgetService } from 'Services/widget';
import leftArrowIconUrl from 'assets/bf-images/left-arrow.svg';
import { ViewTariffResult } from '../../ui/ViewTariffResult';
import { prepareDataForTariffGM, preparedDefaultGroupGM } from '../../garantie-max-hooks';
import { InsuredObjectTypes } from 'Services/widgets/enums';
import { IPolicyObject } from 'Services/widgets/interfaces';
import { IPersonalDataStepGM } from '../PersonalDataStep';
import { blankLink } from 'config';
import { TooltipPopup } from '../../../base-ui-elements/TooltipPopup';
import { JsonValue } from '@cover42/protobuf-util';
import { GMBFStep } from 'App/components/widgets/garantie-max/enum';

export interface TariffSubStep2Props {
  isNotDataSubStep1: boolean;
  productCode: string;
  lead: ILeadData;
  productData: IProductDataGM;
}

export const billingNameField = 'billingFrequencyUnit';
const brokerId = 'brokerID';

export const TariffSubStep2: React.FC<TariffSubStep2Props> = ( tariffProps ) => {
  const { t } = useTranslation( [ 'widgets', 'base' ] );
  const { productCode, lead, productData, isNotDataSubStep1 } = tariffProps;
  const defaultGroup = productData.insuredObjects.find( ( obj ) => obj.name === InsuredObjectTypes.Default );
  const brokerFactors = productData.factors.find( ( factor ) => factor.label === brokerId )?.values!;
  const actions = useGarantieMaxActions();
  const loadingOnFullContainer = useLoadingSpinnerOnFullContainer();
  const { showAlert } = useAppAlertService();
  const service = useWidgetService();
  const [ variable, setVariable ] = React.useState<IBFProductVariable | undefined>( undefined );
  const [ billingField, setBillingField ] = React.useState<IBFProductVariable | undefined>( undefined );
  const [ tariffData, setTariffData ] = React.useState<IPremiumCalculationStateGM | null>( null );
  const [ showTooltip, setShowTooltip ] = React.useState<boolean>( false );
  const [ bfTooltip, setBfTooltip ] = React.useState<string>( '' );

  const [ reloadTariff, setReloadTariff ] = React.useState<number>( dayjs().valueOf() );
  const [ isLoading, setLoading ] = React.useState<boolean>( false );
  const [ isValidCode, setValidCode ] = React.useState<boolean>( false );

  const formContext = useForm( {
    mode: 'onChange',
  } );

  const { formState, getValues, control, errors, setError, clearErrors } = formContext;

  const onSubmit = React.useCallback( async ( formData: ITariffDataStep ) => {
    const result = await service.sendSubstep2Tariff( formData );
    if ( result.errorCode === 0 ) {
      actions.goToStep( 'personal-data', true );
    } else {
      showAlert( {
        message: t( 'base:forms.messages.errorSave' ),
        type: 'danger',
      } );
    }
  }, [ actions, service, showAlert, t ] );

  const onShowTooltip = React.useCallback ( ( isShow: boolean, tooltip: string ): void => {
    setShowTooltip( isShow );
    setBfTooltip( tooltip );
  }, [] );

  React.useEffect( () => {
    let isMounted = true;
    const loadTariff = async () => {
      try {
        const subStep2TariffData = getValues();
        const tariff = { ...lead.subStep1TariffData };

        const preparedData = prepareDataForTariffGM(
          tariff,
          lead.subStep1DeviceItems!,
          productData.insuredObjects,
          false,
        );

        const subStepData = { ...subStep2TariffData };
        const preparedDefaultGroup = preparedDefaultGroupGM(
          subStepData,
          tariff,
          lead.personalData as IPersonalDataStepGM,
          lead.subStep1DeviceItems!,
          productData.insuredObjects,
          false,
        );

        const sendData: IPolicyObject[] = [ ...preparedData, ...preparedDefaultGroup ];

        const payloadInvoice: IProductDataForInvoice = {
          policyObjects: sendData,
        };
        const result = await service.getProductInvoice( productCode, payloadInvoice );
        if ( isMounted ) {
          if ( result ) {
            setTariffData( {
              premiumData: result,
              error: null,
            } );
          } else {
            setTariffData( {
              premiumData: null,
              error: t ( 'base:messages.noData' ),
            } );
          }
          setLoading( false );
        }
      } catch( e ) {
        if ( e instanceof Error ) {
          if ( isMounted ) {
            setTariffData( {
              premiumData: null,
              error: `Error in premium calculation. The reason is: ${e.message}`,
            } );
            setLoading( false );
          }
        } else {
          throw e;
        }
      }
    };
    loadTariff();
    return () => {
      isMounted = false;
    };
  }, [ lead.subStep1TariffData, productCode, service, reloadTariff, getValues, t, productData,
    lead.subStep1DeviceItems, lead.personalData, lead.subStep2TariffData, defaultGroup ] );

  const getFieldValue = ( stringValue: JsonValue, nameField: string ): string => {
    if ( lead.subStep2TariffData !== null && lead.subStep2TariffData[nameField] ) {
      return lead.subStep2TariffData[nameField];
    }

    if ( brokerFactors ) {
      const foundBrokerId = brokerFactors.find( ( val ) => val.key === stringValue );
      if ( foundBrokerId ) {
        if ( foundBrokerId.name ) {
          setValidCode( true );
        }
        return foundBrokerId.name ? foundBrokerId.name : '';
      }
    }

    return stringValue !== null ? stringValue as string : '';
  };

  const addClassToField = ( errorField: string | undefined ): string => {
    if ( errorField !== undefined ) {
      return 'is-invalid';
    }

    if ( isValidCode ) {
      return 'is-valid';
    }

    return '';
  };

  const onValidBrokerId = React.useCallback( async ( fieldName: string, fieldLabel: string ) => {
    setValidCode( false );
    clearErrors( fieldName );
    setLoading( true );

    const brokerVal = getValues( fieldName );

    if ( brokerVal && brokerFactors ) {
      const foundBrokerId = brokerFactors.find( ( val ) => val.name === brokerVal );
      if ( !foundBrokerId ) {
        setError( fieldName, {
          shouldFocus: true,
          message: t( 'bookingFunnel.notFoundBrokerId' ),
        } );
      } else {
        setValidCode( true );
      }
    }

    try {
      const newField = {
        [fieldName]: brokerVal,
      };
      const result = await service.sendSubstep2Tariff( newField );
      if ( result.errorCode === 0 ) {
        setReloadTariff( dayjs().valueOf() );
      }
    } catch( e ) {
      showAlert( {
        message: t( 'base:forms.messages.errorSave' ),
        type: 'danger',
      } );
    }
  }, [ brokerFactors, clearErrors, getValues, service, setError, showAlert, t ] );

  if ( defaultGroup && variable === undefined ) {
    const foundVariable = defaultGroup.productFields?.find( ( field ) => field.name === brokerId );

    if ( foundVariable ) {
      if ( lead.subStep2TariffData !== null && lead.subStep2TariffData[foundVariable.name] ) {
        const foundBrokerCode = brokerFactors.find(
          ( val ) => val.name === lead.subStep2TariffData![foundVariable.name],
        );
        if ( !isValidCode && foundBrokerCode ) {
          setValidCode( true );
        }
      }

      setVariable( foundVariable );
    }
  }

  if ( defaultGroup && billingField === undefined ) {
    const fountBillingField = defaultGroup.productFields?.find( ( field ) => field.name === billingNameField );

    if ( fountBillingField ) {
      setBillingField( fountBillingField );
    }
  }

  if ( isNotDataSubStep1 ) {
    actions.goToSubStep( 'subStep1', false );
  }

  if ( productData.insuredObjects === null ) {
    return <LoadingSpinner />;
  }

  return (
    <FormProvider { ...formContext }>
      <div id="tariff-sub-step2" className="mt-3 col-md-12">
        { isLoading && loadingOnFullContainer }
        <Row className="sticky-top">
          <Col md={ 12 }>
            <button
              id={ `${GMBFStep.TariffSubStep2}_back` }
              type="button"
              className="btn btn-link m-0 p-0 text-c-black f-16 back-button"
              onClick={ () => actions.goToSubStep( 'subStep1', false ) }
            >
              <img src={ leftArrowIconUrl } alt="Back" />
              <span className="back-text pl-3 align-middle">
                { t( 'bookingFunnel.previousBtn' ) }
              </span>
            </button>
          </Col>
        </Row>
        <Row className="max-w-450">
          <Col md={ 12 } className="head-tab text-center px-0">
            <h1 className="f-26 text-c-black">{ t( 'bookingFunnel.tariff.subStep2Title' ) }</h1>
          </Col>
          <Col md={ 12 } className="head-tab mt-4 mb-5 px-0 text-center">
            <h2 className="f-18 text-c-black">{ t( 'bookingFunnel.tariff.subStep2SubTitleGM' ) }</h2>
          </Col>
        </Row>
        <Row>
          <Col className="mt-5 px-0" md={ 12 }>
            <Form noValidate onSubmit={ formContext.handleSubmit( onSubmit ) }>
              <Row className="bf-custom-p-clear col-md-12 m-0 justify-content-center p-0-mob">
                <Col md={ 12 } className="mb-4">
                  <ViewTariffResult
                    tariffData={ tariffData! }
                    lead={ lead }
                    productData={ productData }
                  />
                </Col>
                <Col md={ 12 }>
                  { billingField !== undefined && (
                    <Controller
                      name={ billingField.name }
                      control={ control }
                      rules={ { required: false } }
                      defaultValue={ billingField.defaultValue ? billingField.defaultValue : 'year' }
                      render={ ( props ) => (
                        <FormGroup controlId={ props.name }>
                          <Form.Control
                            { ...props }
                            type="hidden"
                          />
                        </FormGroup>
                      ) }
                    />
                  ) }
                  { variable !== undefined && (
                    <Fragment>
                      <Controller
                        name={ variable.name }
                        control={ control }
                        rules={ { required: variable.isRequired } }
                        defaultValue={
                          getFieldValue( variable.defaultValue, variable.name )
                        }
                        render={ ( props ) => (
                          <FormGroup controlId={ props.name }>
                            <Form.Label className={ variable.bfTooltip && 'tooltip-label' }>
                              { variable.bfLabel ?
                                <div dangerouslySetInnerHTML={ { __html: `${variable.bfLabel}` } }></div>
                                : variable.label }
                              { variable.bfTooltip && (
                                <a
                                  id={ `tooltip-${variable.name}` }
                                  href={ blankLink }
                                  onClick={ ( e ) => {
                                    e.preventDefault();
                                    onShowTooltip( true, variable.bfTooltip! );
                                  } }
                                >
                                  { t( 'bookingFunnel.tooltipHelp' ) }
                                </a>
                              ) }
                            </Form.Label>
                            { variable.bfDescription && (
                              <span className="gm-info f-12 mt-1">
                                { variable.bfDescription }
                              </span>
                            ) }
                            <InputGroup className={ addClassToField( errors[props.name] ) }>
                              <Form.Control
                                { ...props }
                                type="text"
                                placeholder={ variable.label }
                                isInvalid={ errors[props.name] !== undefined }
                                isValid={ isValidCode }
                              />
                              <Button
                                type="button"
                                className="px-4 py-0 m-0 ml-2"
                                variant="primary"
                                onClick={ () => onValidBrokerId( props.name, variable.label ) }
                              >
                                Ok
                              </Button>
                            </InputGroup>
                            <Form.Control.Feedback type="invalid">
                              { errors[props.name]?.message ? (
                                errors[props.name]?.message
                              ) : (
                                <Fragment>
                                  { t( 'base:forms.messages.fieldRequired',
                                    { fieldLabel: variable.label } ) }
                                </Fragment>
                              ) }
                            </Form.Control.Feedback>
                            <Form.Control.Feedback type="valid">
                              { t( 'bookingFunnel.validBrokerIdGM' ) }
                            </Form.Control.Feedback>
                          </FormGroup>
                        ) }
                      />
                      { showTooltip && bfTooltip && (
                        <TooltipPopup
                          tooltipInfo={ bfTooltip }
                          onClose={ () => onShowTooltip( false, '' ) }
                        />
                      ) }
                    </Fragment>

                  ) }
                </Col>
              </Row>
              <Row className="m-0 mt-5">
                <Col sm={ 12 } className="d-flex justify-content-center m-0 p-0">
                  <Button
                    id={ `${GMBFStep.TariffSubStep2}_next` }
                    type="submit"
                    variant="primary"
                    disabled={ formState.isSubmitting }
                    className="mr-0 bg-btn-primary"
                  >
                    <span>
                      { t( 'bookingFunnel.nextBtnGM' ) }
                    </span>
                  </Button>
                </Col>
                <Col sm={ 12 } className="d-flex justify-content-center m-0 p-0 mt-2">
                  <Button
                    id={ `${GMBFStep.TariffSubStep2}_back` }
                    type="button"
                    variant="link"
                    className="mt-2 mb-2 mx-0 py-0 text-c-black custom-back-button"
                    onClick={ () => actions.goToSubStep( 'subStep1', false ) }
                  >
                    { t( 'bookingFunnel.previousBtn' ) }
                  </Button>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </div>
    </FormProvider>
  );
};
